/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import { borderGrey, purple, shadow, universalStyles, black, backgroundPurple } from '../../../../app/theme';
import { IMAGE_PATHS, BASE_URL } from '../../../../constants/paths.constants';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '100%',
  },
  heading: {
    margin: '10px 0px 50px 0px',
  },
  card: {
    display: 'flex',
    boxSizing: 'border-box',
    flexDirection: 'row',
    padding: '19px 71px 29px 21px',
    width: '100%',
    border: `1px solid ${borderGrey}`,
    boxShadow: `3px 3px 4px ${shadow}`,
    marginBottom: 27,
  },
  icon: {
    width: 74,
    height: 74,
  },
  cardTitle: {
    marginBottom: 7,
    paddingBottom: 7,
    fontSize: 22,
    borderBottom: `1px solid ${purple}`,
  },
  cardContent: {
    marginLeft: 17,
  },
  columnWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    marginLeft: 80,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginBottom: 40,
  },
  text: {
    margin: '0 0 12px 0',
  },
  columnImage: {
    marginLeft: 50,
    paddingBottom: 50,
    maxHeight: 1120,
  },
  imageHeader: {
    marginBottom: 8,
    fontSize: 22,
    '& > em': {
      display: 'block',
    },
  },
  imageList: {
    paddingLeft: 30,
    '& > li': {
      marginTop: 8,
    },
  },
  imageColumnWrapper: {
    justifyContent: 'space-around',
  },
  column: {
    boxSizing: 'border-box',
    width: '40%',
  },
  bottomImage: {
    width: '100%',
  },
  caption: {
    marginBottom: 15,
    textAlign: 'center',
    marginTop: 50,
    fontSize: 18,
    fontWeight: 'bold',
  },
  newSection: {
    marginTop: 50,
  },
  checkmark: {
    width: 78,
    height: 78,
    display: 'inline',
    verticalAlign: 'middle',
    paddingTop: 20,
    paddingBottom: 20,
  },
  checkmarkCaption: {
    color: black,
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1.2,
    display: 'inline-block',
    verticalAlign: 'middle',
    textAlign: 'left',
    width: 320,
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 25,
    paddingRight: 25,
  },
  flex: {
    display: 'flex',
    padding: '25px 25px 25px 25px',
    backgroundColor: backgroundPurple,
  },
  flexbox: {
    flex: 1,
    borderRight: `1px solid ${purple}`,
    padding: '0px 25px 0px 25px',
    display: 'flex',
    alignItems: 'center',
  },
  flextxt: {
    flex: 1,
    paddingLeft: 20,
    fontWeight: 600,
    verticalAlign: 'middle',
    marginBottom: 0,
  },
  lastbox: {
    border: 'none',
  },
}));

const AcquisitionPage06 = (): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[1].ACQUISITION_STEP_2.path}`}
          alt={`${IMAGE_PATHS.STEPS[1].ACQUISITION_STEP_2.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 2:</Typography>
          <Typography variant="h3">Prepare the Patient</Typography>
          <Typography variant="h5" className={classes.heading}>
            Important Counseling Points for Patients
          </Typography>
          <div className={classes.card}>
            <img
              className={classes.icon}
              src={`${BASE_URL}${IMAGE_PATHS.EXERCISE_ICON.path}`}
              alt={IMAGE_PATHS.EXERCISE_ICON.alt}
            />
            <div className={classes.cardContent}>
              <Typography variant="h4" className={classes.cardTitle}>
                Avoid exercise 24 hours before injection
              </Typography>
              <ul className={classes.list}>
                <li>Advise patient to avoid any significant exercise for at least one day prior to PET imaging</li>
                <ul className={classNames(classes.list, classes.nestedList)}>
                  <li>
                    If patient has not avoided exercise, biodistribution may be altered, and this should be taken into
                    account during image interpretation
                  </li>
                </ul>
              </ul>
            </div>
          </div>
          <div className={classes.card}>
            <img
              className={classes.icon}
              src={`${BASE_URL}${IMAGE_PATHS.EATING_ICON.path}`}
              alt={IMAGE_PATHS.EATING_ICON.alt}
            />
            <div className={classes.cardContent}>
              <Typography variant="h4" className={classes.cardTitle}>
                Avoid eating or drinking 4 hours before injection
              </Typography>
              <ul className={classes.list}>
                <li>
                  Advise patients not to eat or drink for at least 4 hours (other than sips of water for taking
                  medications) prior to administration of Axumin
                </li>
                <ul className={classNames(classes.list, classes.nestedList)}>
                  <li>
                    If patient has not fasted, biodistribution may be altered, and this should be taken into account
                    during image interpretation
                  </li>
                </ul>
              </ul>
            </div>
          </div>
          <div className={classes.card}>
            <img
              className={classes.icon}
              src={`${BASE_URL}${IMAGE_PATHS.VOID_ICON.path}`}
              alt={IMAGE_PATHS.VOID_ICON.alt}
            />
            <div className={classes.cardContent}>
              <Typography variant="h4" className={classes.cardTitle}>
                Void approximately 30 to 60 minutes before scanning
              </Typography>
              <ul className={classes.list}>
                <li>
                  <span>
                    Advise patients to void approximately <b>30 minutes to 60 minutes </b> prior to administration of
                    Axumin and then refrain from voiding until after the scan has been completed
                  </span>
                </li>
                <ul className={classNames(classes.list, classes.nestedList)}>
                  <li>
                    If patient voids within 30 minutes prior to start of scan, there is possibility of early appearance
                    of bladder activity, and this should be taken into account during image interpretation. This
                    activity is usually generalized, but occasionally may be focal in nature (simulating appearance of a
                    nodule adjacent to the bladder wall), at least on the early images
                  </li>
                </ul>
              </ul>
            </div>
          </div>
          <Typography variant="h5" className={classNames(classes.heading, classes.newSection)}>
            Considerations for Technologists
          </Typography>
          <div className={classNames(classes.flex)}>
            <div className={classNames(classes.flexbox)}>
              <img
                className={classNames(classes.checkmark)}
                src={`${BASE_URL}${IMAGE_PATHS.YELLOW_CHECKMARK.path}`}
                alt={`${IMAGE_PATHS.YELLOW_CHECKMARK.alt}`}
              />
              <Typography className={classNames(classes.text, classes.flextxt)}>
                Did the patient exercise in the last 24 hours?
              </Typography>
            </div>
            <div className={classNames(classes.flexbox)}>
              <img
                className={classNames(classes.checkmark)}
                src={`${BASE_URL}${IMAGE_PATHS.YELLOW_CHECKMARK.path}`}
                alt={`${IMAGE_PATHS.YELLOW_CHECKMARK.alt}`}
              />
              <Typography className={classNames(classes.text, classes.flextxt)}>
                Has the patient had nothing by mouth (NPO) for 4 hours?
              </Typography>
            </div>
            <div className={classNames(classes.flexbox, classes.lastbox)}>
              <img
                className={classNames(classes.checkmark)}
                src={`${BASE_URL}${IMAGE_PATHS.YELLOW_CHECKMARK.path}`}
                alt={`${IMAGE_PATHS.YELLOW_CHECKMARK.alt}`}
              />
              <Typography className={classNames(classes.text, classes.flextxt)}>
                What time did the patient last void?
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AcquisitionPage06;
